/** -------------------------------------------------------------------------- *
 * @file   Koyomi.jsx
 * @author FST.EZAKI <ezaki@furunosoftech.co.jp>
 * @brief  暦計算
 * @note   参考資料
 *         ・長沢 工「天体の位置計算 増補版」
 *           ISBN4-8052-0225-4 C3044
 *         ・水路部式で太陽黄経・月黄経を計算し、朔弦望・二十四節気を求める
 *           http://omatsuja2.blogspot.com/2021/02/calcLongitude.html
 *         ・旧暦カレンダー
 *           https://www.arachne.jp/onlinecalendar/kyureki/
 *         ・旧暦の月の決め方
 *           http://koyomi8.com/reki_doc/doc_0101.htm
 * --------------------------------------------------------------------------- */

/*==============================================================================
 * Inport
 *=============================================================================*/

/*==============================================================================
 * Constant
 *=============================================================================*/
const INF = Infinity;   /** 無限大（短縮形） */

/** -------------------------------------------------------------------------- *
 * @brief Table of Espenak, Meeus (2004)
 * @note  https://eclipse.gsfc.nasa.gov/SEcat5/deltatpoly.html
 * --------------------------------------------------------------------------- */
const EM2004 = [
	{L: -INF, H: -500, B: 1820, U: 100, a: [-20, 0, 32]},
	{L: -500, H:  500, B:    0, U: 100, a: [10583.6, -1014.41, 33.78311, -5.952053, -0.1798452, 0.022174192, 0.0090316521]},
	{L:  500, H: 1600, B: 1000, U: 100, a: [1574.2, -556.01, 71.23472, 0.319781, -0.8503463, -0.005050998, 0.0083572073]},
	{L: 1600, H: 1700, B: 1600, U:   1, a: [120, -0.9808, -0.01532, 1 / 7129]},
	{L: 1700, H: 1800, B: 1700, U:   1, a: [8.83, 0.1603, -0.0059285, 0.00013336, -1 / 1174000]},
	{L: 1800, H: 1860, B: 1800, U:   1, a: [13.72, -0.332447, 0.0068612, 0.0041116, -0.00037436, 0.0000121272, -1.699e-7, 8.75e-10]},
	{L: 1860, H: 1900, B: 1860, U:   1, a: [7.62, 0.5737, -0.251754, 0.01680668, -0.0004473624, 1 / 233174]},
	{L: 1900, H: 1920, B: 1900, U:   1, a: [-2.79, 1.494119, -0.0598939, 0.0061966, -0.000197]},
	{L: 1920, H: 1941, B: 1920, U:   1, a: [21.2, 0.84493, -0.0761, 0.0020936]},
	{L: 1941, H: 1961, B: 1950, U:   1, a: [29.07, 0.407, -1 / 233, 1 / 2547]},
	{L: 1961, H: 1986, B: 1975, U:   1, a: [45.45, 1.067, -1 / 260, -1 / 718]},
	{L: 1986, H: 2005, B: 2000, U:   1, a: [63.86, 0.3345, -0.060374, 0.0017275, 0.000651814, 2.373599e-5]},
	{L: 2005, H: 2050, B: 2000, U:   1, a: [62.92, 0.32217, 0.005589]},
	{L: 2050, H: 2150, B: 1820, U: 100, a: [-205.724, 56.28, 32]},
	{L: 2150, H:  INF, B: 1820, U: 100, a: [-20, 0, 32]},
];

/** -------------------------------------------------------------------------- *
 * @brief 基準日[日]
 * --------------------------------------------------------------------------- */
const BASE_T = (new Date('1974-12-31T00:00:00Z')).getTime() / 86400000;

/*==============================================================================
 * Program
 *=============================================================================*/
/** -------------------------------------------------------------------------- *
 * @brief  Espenak and Meeus(2004)に基づき暦表時との時間差ΔT[sec]を求める
 * @param  世界時（UT）（Dateオブジェクト）
 * @return 時間差ΔT[sec]
 * @note   水路部式の誤差が大きくなってきているので、こっちの方がマシらしい
 * --------------------------------------------------------------------------- */
export function getDeltaT(date = new Date()) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    
    const {B, U, a} = EM2004.filter((item) => ((item.L <= year) && (year < item.H)))[0];
    const y = year + (month - 0.5) / 12;
    const u = (y -B) / U;
    const dT = a.reduce((sum, v, i) => (sum + v*u**i), 0);
    return dT;
}

/** -------------------------------------------------------------------------- *
 * @brief  暦表示（ET）を求める
 * @param  date [in] : 世界時（UT）（Dateオブジェクト）
 * @return 暦表示（Dateオブジェクト）
 * --------------------------------------------------------------------------- */
export function getET(date = new Date()) {
    return new Date(date.getTime() + getDeltaT(date)*1000);
}

/** -------------------------------------------------------------------------- *
 * @brief  水路部式で使用する「t」を求める
 * @param  date [in] : 世界時（UT）（Dateオブジェクト）
 * @return t
 * @note   t：1975-01-00T00:00:00（ET）を原点0とし、365.25日を単位量1とする値
 * --------------------------------------------------------------------------- */
export function getT(date = new Date()) {
    const ET = getET(date).getTime() / 86400000;
    const t = (ET - BASE_T) / 365.25;
    return t;
}

/** -------------------------------------------------------------------------- *
 * @brief  度→ラジアン変換
 * @param  degree [in] : 度[deg]
 * @return ラジアン値
 * --------------------------------------------------------------------------- */
const toRadians = (degree) => (degree * (Math.PI/180));

/** -------------------------------------------------------------------------- *
 * @brief  度単位でのsin関数
 * @param  deg [in] : 角度[deg]
 * @return sin(deg)の値
 * --------------------------------------------------------------------------- */
const sinDeg = (deg) => Math.sin(toRadians(deg));

/** -------------------------------------------------------------------------- *
 * @brief  角度を0～360度に丸める（360は含まない）
 * @param  deg [in] : 角度
 * @return 0～360度に丸めた角度[deg]
 * --------------------------------------------------------------------------- */
export const normalized360 = (deg) => {
    if(deg < 0){
        deg += Math.trunc(-deg/360 + 1)*360;
    }
    return deg % 360;
};

/** -------------------------------------------------------------------------- *
 * @brief 太陽の位置の概略式テーブル
 * --------------------------------------------------------------------------- */
const TABLE_SUN = [
    (t) => (                              (+279.0358 +  360.00769*t)),
    (t) => (+(1.9159 - 0.00005*t) * sinDeg(+356.531  +  359.991  *t)),
    (t) => (+0.0200               * sinDeg(+353.06   +  719.981  *t)),
    (t) => (-0.0048               * sinDeg(+248.64   -   19.341  *t)),
    (t) => (+0.0020               * sinDeg(+285.0    +  329.64   *t)),
    (t) => (+0.0018               * sinDeg(+334.2    - 4452.67   *t)),
    (t) => (+0.0018               * sinDeg(+293.7    -    0.20   *t)),
    (t) => (+0.0015               * sinDeg(+242.4    +  450.37   *t)),
    (t) => (+0.0013               * sinDeg(+211.1    +  225.18   *t)),
    (t) => (+0.0008               * sinDeg(+248.4    +  659.29   *t)),
    (t) => (+0.0007               * sinDeg(+ 53.5    +   90.38   *t)),
    (t) => (+0.0007               * sinDeg(+ 12.1    -   30.35   *t)),
    (t) => (+0.0006               * sinDeg(+239.1    +  337.18   *t)),
    (t) => (+0.0005               * sinDeg(+ 10.1    -    1.50   *t)),
    (t) => (+0.0005               * sinDeg(+ 99.1    -   22.81   *t)),
    (t) => (+0.0004               * sinDeg(+264.8    +  315.56   *t)),
    (t) => (+0.0004               * sinDeg(+233.8    +  299.30   *t)),
    (t) => (-0.0004               * sinDeg(+198.1    +  720.02   *t)),
    (t) => (+0.0003               * sinDeg(+349.6    + 1079.97   *t)),
    (t) => (+0.0003               * sinDeg(+ 65.0    -   44.43   *t)),
];

/** -------------------------------------------------------------------------- *
 * @brief  太陽の視黄経λsを求める
 * @param  date [in] : 世界時（UT）（Dateオブジェクト）
 * @return 視黄経λs[deg]
 * --------------------------------------------------------------------------- */
export function getLambdaSun(date = new Date()) {
    const t = getT(date);
    const ls = TABLE_SUN.reduce(
        (sum, func) => (sum + func(t)),
        0
    );
    return normalized360(ls);
}

/** -------------------------------------------------------------------------- *
 * @brief 月の位置の概略式テーブル（補正項A）
 * --------------------------------------------------------------------------- */
const TABLE_A = [
    (t) => (+0.0040 * sinDeg( 93.8 - 1.33 *t)),
    (t) => (+0.0020 * sinDeg(248.6 - 19.34*t)),
    (t) => (+0.0006 * sinDeg( 66   +  0.2 *t)),
    (t) => (+0.0006 * sinDeg(249   - 19.3 *t)),
];

const A = (t) => TABLE_A.reduce(
    (sum, func) => (sum + func(t)),
    0
);

/** -------------------------------------------------------------------------- *
 * @brief 月の位置の概略式テーブル
 * --------------------------------------------------------------------------- */
const TABLE_MOON = [
    (t) => (                 (+124.8754 +  4812.67881*t)),
    (t) => (+6.2887  * sinDeg(+338.915  +  4771.9886 *t + A(t))),
    (t) => (+1.2740  * sinDeg(+107.248  -  4133.3536 *t)),
    (t) => (+0.6583  * sinDeg(+ 51.668  +  8905.3422 *t)),
    (t) => (+0.2136  * sinDeg(+317.831  +  9543.9773 *t)),
    (t) => (+0.1856  * sinDeg(+176.531  +   359.9905 *t)),
    (t) => (+0.1143  * sinDeg(+292.463  +  9664.0404 *t)),
    (t) => (+0.0588  * sinDeg(+ 86.16   +   638.635  *t)),
    (t) => (+0.0572  * sinDeg(+103.78   -  3773.363  *t)),
    (t) => (+0.0533  * sinDeg(+ 30.58   + 13677.331  *t)),
    (t) => (+0.0459  * sinDeg(+124.86   -  8545.352  *t)),
    (t) => (+0.0410  * sinDeg(+342.38   +  4411.998  *t)),
    (t) => (+0.0348  * sinDeg(+ 25.83   +  4452.671  *t)),
    (t) => (+0.0305  * sinDeg(+155.45   +  5131.979  *t)),
    (t) => (+0.0153  * sinDeg(+240.79   +   758.698  *t)),
    (t) => (+0.0125  * sinDeg(+271.38   + 14436.029  *t)),
    (t) => (+0.0110  * sinDeg(+226.45   -  4892.052  *t)),
    (t) => (+0.0107  * sinDeg(+ 55.58   - 13038.696  *t)),
    (t) => (+0.0100  * sinDeg(+296.75   + 14315.966  *t)),
    (t) => (+0.0085  * sinDeg(+ 34.5    -  8266.71   *t)),
    (t) => (+0.0079  * sinDeg(+290.7    -  4493.34   *t)),
    (t) => (+0.0068  * sinDeg(+228.2    +  9265.33   *t)),
    (t) => (+0.0052  * sinDeg(+133.1    +   319.32   *t)),
    (t) => (+0.0050  * sinDeg(+202.4    +  4812.66   *t)),
    (t) => (+0.0048  * sinDeg(+ 68.6    -    19.34   *t)),
    (t) => (+0.0040  * sinDeg(+ 34.1    + 13317.34   *t)),
    (t) => (+0.0040  * sinDeg(+  9.5    + 18449.32   *t)),
    (t) => (+0.0040  * sinDeg(+ 93.8    -     1.33   *t)),
    (t) => (+0.0039  * sinDeg(+103.3    + 17810.68   *t)),
    (t) => (+0.0037  * sinDeg(+ 65.1    +  5410.62   *t)),
    (t) => (+0.0027  * sinDeg(+321.3    +  9183.99   *t)),
    (t) => (+0.0026  * sinDeg(+174.8    - 13797.39   *t)),
    (t) => (+0.0024  * sinDeg(+ 82.7    +   998.63   *t)),
    (t) => (+0.0024  * sinDeg(+  4.7    +  9224.66   *t)),
    (t) => (+0.0022  * sinDeg(+121.4    -  8185.36   *t)),
    (t) => (+0.0021  * sinDeg(+134.4    +  9903.97   *t)),
    (t) => (+0.0021  * sinDeg(+173.1    +   719.98   *t)),
    (t) => (+0.0021  * sinDeg(+100.3    -  3413.37   *t)),
    (t) => (+0.0020  * sinDeg(+248.6    -    19.34   *t)),
    (t) => (+0.0018  * sinDeg(+ 98.1    +  4013.29   *t)),
    (t) => (+0.0016  * sinDeg(+344.1    + 18569.38   *t)),
    (t) => (+0.0012  * sinDeg(+ 52.1    - 12678.71   *t)),
    (t) => (+0.0011  * sinDeg(+250.3    + 19208.02   *t)),
    (t) => (+0.0009  * sinDeg(+ 81      -  8586.0    *t)),
    (t) => (+0.0008  * sinDeg(+207      + 14037.3    *t)),
    (t) => (+0.0008  * sinDeg(+ 31      -  7906.7    *t)),
    (t) => (+0.0007  * sinDeg(+346      +  4052.0    *t)),
    (t) => (+0.0007  * sinDeg(+294      -  4853.3    *t)),
    (t) => (+0.0007  * sinDeg(+ 90      +   278.6    *t)),
    (t) => (+0.0006  * sinDeg(+237      +  1118.7    *t)),
    (t) => (+0.0005  * sinDeg(+ 82      + 22582.7    *t)),
    (t) => (+0.0005  * sinDeg(+276      + 19088.0    *t)),
    (t) => (+0.0005  * sinDeg(+ 73      - 17450.7    *t)),
    (t) => (+0.0005  * sinDeg(+112      +  5091.3    *t)),
    (t) => (+0.0004  * sinDeg(+116      -   398.7    *t)),
    (t) => (+0.0004  * sinDeg(+ 25      -   120.1    *t)),
    (t) => (+0.0004  * sinDeg(+181      +  9584.7    *t)),
    (t) => (+0.0004  * sinDeg(+ 18      +   720.0    *t)),
    (t) => (+0.0003  * sinDeg(+ 60      -  3814.0    *t)),
    (t) => (+0.0003  * sinDeg(+ 13      -  3494.7    *t)),
    (t) => (+0.0003  * sinDeg(+ 13      + 18089.3    *t)),
    (t) => (+0.0003  * sinDeg(+152      +  5492.0    *t)),
    (t) => (+0.0003  * sinDeg(+317      -    40.7    *t)),
    (t) => (+0.0003  * sinDeg(+348      + 23221.3    *t)),
];

/** -------------------------------------------------------------------------- *
 * @brief  月の視黄経λmを求める 
 * @param  date [in] : 世界時（UT）（Dateオブジェクト）
 * @return 視黄経λm[deg]
 * --------------------------------------------------------------------------- */
export function getLambdaMoon(date = new Date()) {
    const t = getT(date);
    const lm = TABLE_MOON.reduce(
        (sum, func) => (sum + func(t)),
        0
    );
    return normalized360(lm);
}

/** -------------------------------------------------------------------------- *
 * @brief  月の視黄経と太陽の視黄経の離角Δλを求める
 * @param  date [in] : 世界時（UT）（Dateオブジェクト）
 * @return 離角Δλ[deg]
 * --------------------------------------------------------------------------- */
export function getDeltaLambda(date = new Date())
{
    return normalized360(getLambdaMoon(date) - getLambdaSun(date));
}

/** -------------------------------------------------------------------------- *
 * @brief  基準日から月の視黄経と太陽の視黄経の差がある値になった日付を求める
 * @param  deg      [in] : 月の視黄経と太陽の視黄経の差[deg]
 * @param  baseDate [in] : 基準日（UT）（Dateオブジェクト）
 * @param  toFuture [in] : 計算する時間の向き（true：未来、false：過去）
 * @return 日付（UT）（Dateオブジェクト）
 * --------------------------------------------------------------------------- */
export function getDeltaLambdaDate(deg = 0, baseDate = new Date(), toFuture = false)
{
    let date = baseDate;
    let count = 0;
    let s = 0;
    const dir = toFuture ? 1 : -1;
    do{
        count++;
        s = normalized360( dir*(deg - getDeltaLambda(date)) );
        date = new Date(date.getTime() + dir*20*(s)/360*86400*1000);
    }while ((s > 0.001) && (count < 30));
    return date;
}

/** -------------------------------------------------------------------------- *
 * @brief  基準日から太陽の視黄経がある値になった日付を求める
 * @param  deg      [in] : 太陽の視黄経[deg]
 * @param  baseDate [in] : 基準日（UT）（Dateオブジェクト）
 * @param  toFuture [in] : 計算する時間の向き（true：未来、false：過去）
 * @return 日付（UT）（Dateオブジェクト）
 * --------------------------------------------------------------------------- */
export function getSunDate(deg = 0, baseDate = new Date(), toFuture = false)
{
    let date = baseDate;
    let count = 0;
    let s = 0;
    const dir = toFuture ? 1 : -1;
    do{
        count++;
        s = normalized360( dir*(deg - getLambdaSun(date)) );
        date = new Date(date.getTime() + dir*300*(s)/360*86400*1000);
    }while ((s > 0.001) && (count < 30));
    return date;
}


/** -------------------------------------------------------------------------- *
 * @brief  Dateオブジェクトの時刻を設定する
 * @param  date [in] : Dateオブジェクト
 * @param  hours        [in] : 時(0-23)
 * @param  minutes      [in] : 分(0-59)
 * @param  seconds      [in] : 秒(0-59)
 * @param  milliseconds [in] : ミリ秒(0-999)
 * @return 時刻を設定したDateオブジェクト
 * --------------------------------------------------------------------------- */
function Date_setTime(date, hours=0, minutes=0, seconds=0, milliseconds=0) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, seconds, milliseconds);
}

/** -------------------------------------------------------------------------- *
 * @brief  Dateオブジェクトを加算する
 * @param  date [in] : Dateオブジェクト
 * @param  time [in] : 時間[ミリ秒]（マイナスなら減算）
 * @return 時間を加算したDateオブジェクト
 * --------------------------------------------------------------------------- */
function Date_add(date, time) {
    return new Date(date.getTime() + time);
}

/** -------------------------------------------------------------------------- *
 * @brief  日をミリ秒に変換する
 * @param  date [in] : 日
 * @return ミリ秒
 * --------------------------------------------------------------------------- */
const DateToMSec = (date) => (date*86400000);

/** -------------------------------------------------------------------------- *
 * @brief  ミリ秒を日に変換する
 * @param  milliseconds [in] : ミリ秒
 * @return 日（時分秒は小数）
 * --------------------------------------------------------------------------- */
const MSecToDate = (milliseconds) => (milliseconds/86400000);

/** -------------------------------------------------------------------------- *
 * @brief  その日の初めのDateオブジェクトを返す
 * @param  date [in] : Dateオブジェクト
 * @return 時刻を00時00分00.000秒にしたDateオブジェクト
 * --------------------------------------------------------------------------- */
function beginOfDay(date) {
    return Date_setTime(date, 0, 0, 0, 0);
}

/** -------------------------------------------------------------------------- *
 * @brief  その日のsaig初めのDateオブジェクトを返す
 * @param  date [in] : Dateオブジェクト
 * @return 時刻を23時59分59.999秒にしたDateオブジェクト
 * --------------------------------------------------------------------------- */
function endOfDay(date) {
    return Date_setTime(date, 23, 59, 59, 999);
}

/** -------------------------------------------------------------------------- *
 * @brief 旧暦クラス
 * @note  メソッド名はDateクラスに（ある程度）合わせてある
 * --------------------------------------------------------------------------- */
export class KyuuReki {
    /** ---------------------------------------------------------------------- *
     * @brief  コンストラクタ
     * @param  date [in] : 世界時（UT）（Dateオブジェクト）
     * ----------------------------------------------------------------------- */
    constructor(date = new Date()) {
        /* 計算日の最後の時刻 */
        const dateEnd = endOfDay(date);
        
        /* 前の朔の日時を求める */
        const last = getDeltaLambdaDate(0, dateEnd);
        /* 次の朔の日時を求める（前の朔の35日後から遡って朔を探す） */
        const next = getDeltaLambdaDate(0, Date_add(last, DateToMSec(35)));
        
        /* 朔の太陽黄経を求める */
        const lastSun = getLambdaSun(last);
        const nextSun = getLambdaSun(next);
        
        /* 太陽黄経から月を求める（月の値は0-11） */
        const lastMonth = (Math.trunc(lastSun/30) + 1) % 12;
        const nextMonth = (Math.trunc(nextSun/30) + 1) % 12;
        
        /* 旧暦が11月以降で新暦が2月以前なら、旧暦はまだ年をまたいでいないと判断 */
        this.year = date.getFullYear() - (((nextMonth >= 10) && (date.getMonth() < 2)) ? 1 : 0);
        this.isLeapMonth = (lastMonth === nextMonth);
        this.month = nextMonth;
        this.date = MSecToDate(beginOfDay(date).getTime() - beginOfDay(last).getTime()) + 1;
    }
    
    /** ---------------------------------------------------------------------- *
     * @brief 年
     * ----------------------------------------------------------------------- */
    getFullYear() {
        return this.year;
    }
    
    /** ---------------------------------------------------------------------- *
     * @brief 閏月判定
     * ----------------------------------------------------------------------- */
    isLeapMonth() {
        return this.isLeapMonth;
    }
    
    /** ---------------------------------------------------------------------- *
     * @brief 月
     * ----------------------------------------------------------------------- */
    getMonth() {
        return this.month;
    }
    
    /** ---------------------------------------------------------------------- *
     * @brief 日
     * ----------------------------------------------------------------------- */
    getDate() {
        return this.date;
    }
    
    /** ---------------------------------------------------------------------- *
     * @brief 文字列形式に変換する
     * ----------------------------------------------------------------------- */
    toString(format = '{y}/{m}/{d}') {
        return format.replace('{y}', `${this.year}`)
            .replace('{m}', `${this.isLeapMonth ? '閏' : ''}${this.month + 1}`)
            .replace('{d}', `${this.date}`);
    }
}

/** -------------------------------------------------------------------------- *
 * @brief 潮回りコード
 * --------------------------------------------------------------------------- */
const SHIO_CODE_OO   = 0;   /** 大潮 */
const SHIO_CODE_NAKA = 1;   /** 中潮 */
const SHIO_CODE_KO   = 2;   /** 小潮 */
const SHIO_CODE_NAGA = 3;   /** 長潮 */
const SHIO_CODE_WAKA = 4;   /** 若潮 */

/** -------------------------------------------------------------------------- *
 * @brief  潮回りを取得する
 * @param  kr [in] : 旧暦
 * @return 潮回りコード
 * --------------------------------------------------------------------------- */
export function getShioCode(kr = new KyuuReki())
{
    let code = -1;
    switch(kr.getDate()){
        case 3:
        case 4:
        case 5:
        case 6:
        case 12:
        case 13:
        case 18:
        case 19:
        case 20:
        case 21:
        case 27:
        case 28:
            code = SHIO_CODE_NAKA;
            break;
        case 7:
        case 8:
        case 9:
        case 22:
        case 23:
        case 24:
            code = SHIO_CODE_KO;
            break;
        case 10:
        case 25:
            code = SHIO_CODE_NAGA;
            break;
        case 11:
        case 26:
            code = SHIO_CODE_WAKA;
            break;
        default:
            code = SHIO_CODE_OO;
            break;
    }
    return code;
}

/** -------------------------------------------------------------------------- *
 * @brief  日付が同じか判定する（時刻の差は無視）
 * @param  d1 [in] : Dateオブジェクト
 * @param  d2 [in] : Dateオブジェクト
 * @return 日付が同じであれば、true
 * --------------------------------------------------------------------------- */
function isEqualDate(d1, d2) {
    return (d1.getDate() === d2.getDate())
        && (d1.getMonth() === d2.getMonth())
        && (d1.getFullYear() === d2.getFullYear());
}

/** -------------------------------------------------------------------------- *
 * @brief  暦の取得
 * @param  date [in] : 対象日
 * @return その日暦オブジェクト
 * --------------------------------------------------------------------------- */
export function getKoyomi(date = new Date()) {
    /* その日の正午を基準とする */
    const md = Date_setTime(date, 12);
    
    /* 旧暦 */
    const kr = new KyuuReki(md);
    
    /* 六曜（先勝→友引→先負→仏滅→大安→赤口） */
    const you = (kr.getMonth() + kr.getDate() - 1) % 6;
    
    /* 十干（甲→乙→丙→丁→戊→己→庚→辛→壬→癸） */
    const d = Math.trunc(MSecToDate(md.getTime()));  /* 通日 */
    const kan = ((d + 7) % 10);
    
    /* 十二支（子→丑→寅→卯→辰→巳→午→未→申→酉→戌→亥） */
    const shi = ((d + 5) % 12);
    
    /* 十二直（建→除→満→平→定→執→破→危→成→納→開→閉） */
    /* 解説：http://eco.mtk.nao.ac.jp/koyomi/wiki/BDBDC6F3C4BE.html#:~:text=%E5%8D%81%E4%BA%8C%E7%9B%B4%E3%81%A8%E3%81%AF,12%E5%80%8B%E3%81%8B%E3%82%89%E3%81%AA%E3%82%8A%E3%81%BE%E3%81%99%E3%80%82 */
    /*       https://www.nnh.to/yomikata/12choku.html */
    /* 検算：https://keisan.casio.jp/exec/system/1189849966 */
    const ed = endOfDay(date);
    const sekki = Math.trunc(getLambdaSun(ed) / 15);   /* 二十四節気：立春(0)→大寒(23) */
    const odoru = Math.trunc((sekki+1) / 2);    /* 節ごとに「おどる」 */
    const tyoku = ((shi + 21 - odoru) % 12);
    
    /* 二十八宿 */
    /*（角→亢→氐→房→心→尾→箕→斗→牛→女→虚→危→室→壁→奎→婁→胃→昴→畢→觜→参→井→鬼→柳→星→張→翼→軫） */
    /* 解説：https://ja.wikipedia.org/wiki/%E4%BA%8C%E5%8D%81%E5%85%AB%E5%AE%BF */
    /* 検算：https://keisan.casio.jp/exec/system/1189849966 */
    const syuku = ((d + 7) % 28);
    
    /* 潮回り */
    const shio = getShioCode(kr);
    
    /* 太陽黄経 */
    const sun = getLambdaSun(md);
    
    /* 月と太陽の離角（月の満ち欠け） */
    const moon = getDeltaLambda(md);
    
    /* 月齢 */
    const saku = getDeltaLambdaDate(0, md);
    const moonAge = MSecToDate(md.getTime() - saku.getTime());
    
    /* その他 */
    const koty = getKoyomiOTY(date.getFullYear());
    
    return {
        /* 旧暦 */
        KyuuReki: kr,
        /* 六曜 */
        You: {
            code: you,
            text: ['先勝', '友引', '先負', '仏滅', '大安', '赤口'][you]
        },
        /* 十干 */
        Kan: {
            code: kan,
            text: ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'][kan],
            kana: ['きのえ', 'きのと', 'ひのえ', 'ひのと', 'つちのえ', 'つちのと',
                   'かのえ', 'かのと', 'みずのえ', 'みずのと'][kan]
        },
        /* 十二支 */
        Shi: {
            code: shi,
            text: ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'][shi],
            kana: ['ね', 'うし', 'とら', 'う',  'たつ', 'み', 'うま', 'ひつじ', 'さる',  'とり', 'いぬ', 'い'][shi]
        },
        /* 十二直 */
        Tyoku: {
            code: tyoku,
            text: ['建', '除', '満', '平', '定', '執', '破', '危', '成', '納', '開', '閉'][tyoku],
            kana: ['たつ', 'のぞく', 'みつ', 'たいら', 'さだん', 'とる', 'やぶる', 'あやぶ', 'なる', 'おさん', 'ひらく', 'とづ'][tyoku]
        },
        /* 二十八宿 */
        Syuku: {
            code: syuku,
            text: ['角', '亢', '氐', '房', '心', '尾', '箕', '斗', '牛', '女', '虚', '危', '室', '壁', '奎', '婁', '胃', '昴', '畢', '觜', '参', '井', '鬼', '柳', '星', '張', '翼', '軫'][syuku],
            kana: ['かく', 'こう', 'てい', 'ぼう', 'しん', 'び', 'き', 'と', 'ぎゅう', 'じょ', 'きょ', 'き', 'しつ', 'へき', 'けい', 'ろう', 'い', 'ぼう', 'ひつ', 'し', 'しん', 'せい', 'き', 'りゅう', 'せい', 'ちょう', 'よく', 'しん'][syuku]
        },
        /* 潮周り */
        Shio: {
            code: shio,
            text: ['大潮', '中潮', '小潮', '長潮', '若潮'][shio]
        },
        /* 太陽 */
        Sun: {
            degree: sun
        },
        /* 月 */
        Moon: {
            degree: moon,
            age: moonAge,
            text: ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'][Math.trunc(normalized360(moon + 22.5) / 45)]
        },
        /* その他 */
        Appendix: {
            Sun: koty.Sun.filter((item) => isEqualDate(item.date, date))[0]?.text,
            Moon: koty.Moon.filter((item) => isEqualDate(item.date, date))[0]?.text,
            ZasSetsu: koty.ZasSetsu.filter((item) => isEqualDate(item.date, date))[0]?.text
        }
    };
}

/** -------------------------------------------------------------------------- *
 * @brief 年間の暦キャシュ
 * --------------------------------------------------------------------------- */
const KOTY_CACHE = {};

/** -------------------------------------------------------------------------- *
 * @brief  年間暦の取得
 * @param  year : 対象年
 * @return その年の年間暦オブジェクト
 * --------------------------------------------------------------------------- */
export function getKoyomiOTY(year = new Date().getFullYear()) {
    if (KOTY_CACHE[year]) {
        //console.log('cache');
        return KOTY_CACHE[year];
    }
    let date, degree;
    
    /* 月の満ち欠け */
    const Moon = [];
    date = new Date(year, 0, 1);
    degree = Math.trunc(getDeltaLambda(date) / 90) * 90;
    do {
        degree = normalized360(degree + 90);
        date = getDeltaLambdaDate(degree, date, true);
        if (date.getFullYear() === year) {
            Moon.push({
                date,
                text:['朔', '上弦', '望', '下弦'][degree/90],
                degree
            });
        }
    } while (date.getFullYear() === year);
    
    /* 太陽黄経 */
    const Sun = [];
    date = new Date(year, 0, 1);
    degree = Math.trunc(getLambdaSun(date) / 15) * 15;
    do {
        degree = normalized360(degree + 15);
        date = getSunDate(degree, date, true);
        if (date.getFullYear() === year) {
            Sun.push({
                date,
                text:['立春', '雨水', '啓蟄', '春分',
                      '清明', '穀雨', '立夏', '小満',
                      '芒種', '夏至', '小暑', '大暑',
                      '立秋', '処暑', '白露', '秋分',
                      '寒露', '霜降', '立冬', '小雪',
                      '大雪', '冬至', '小寒', '大寒'][Math.trunc(normalized360(degree + 45) / 15)],
                degree
            });
        }
    } while (date.getFullYear() === year);
    
    /* 雑節 */
    /* https://eco.mtk.nao.ac.jp/koyomi/yoko/2022/rekiyou222.html */
    const ZasSetsu = [];
    /* 立春基準 */
    const riSyun = Sun.filter((item) => (item.degree === 315))[0].date;
    date = Date_add(riSyun, DateToMSec(-1));
    ZasSetsu.push({date, text: '節分'});
    date = Date_add(riSyun, DateToMSec(87));
    ZasSetsu.push({date, text: '八十八夜'});
    date = Date_add(riSyun, DateToMSec(210));
    ZasSetsu.push({date, text: '二百十日'});
    date = Date_add(riSyun, DateToMSec(220));
    ZasSetsu.push({date, text: '二百二十日'});
    /* 春分基準 */
    const syunBun = Sun.filter((item) => (item.degree === 0))[0].date;
    date = Date_add(syunBun, DateToMSec(-3));
    ZasSetsu.push({date, text: '彼岸入り'});
    date = Date_add(syunBun, DateToMSec(3));
    ZasSetsu.push({date, text: '彼岸明け'});
    /* 秋分基準 */
    const syuuBun = Sun.filter((item) => (item.degree === 180))[0].date;
    date = Date_add(syuuBun, DateToMSec(-3));
    ZasSetsu.push({date, text: '彼岸入り'});
    date = Date_add(syuuBun, DateToMSec(3));
    ZasSetsu.push({date, text: '彼岸明け'});
    /* 太陽黄経基準 */
    date = new Date(year, 0, 1);
    ZasSetsu.push({date: getSunDate(297, date, true), text: '土用入り'});
    ZasSetsu.push({date: getSunDate( 27, date, true), text: '土用入り'});
    ZasSetsu.push({date: getSunDate( 80, date, true), text: '入梅'});
    ZasSetsu.push({date: getSunDate(100, date, true), text: '半夏生'});
    ZasSetsu.push({date: getSunDate(117, date, true), text: '土用入り'});
    ZasSetsu.push({date: getSunDate(207, date, true), text: '土用入り'});

    /* その他 */
    /* http://koyomi.vis.ne.jp/sub/rekicyuu.htm */
    /* https://www.ndl.go.jp/koyomi/chapter3/s6.html */

    KOTY_CACHE[year] = {
        Sun,
        Moon,
        ZasSetsu
    };
    return KOTY_CACHE[year];
}
